import React, { useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Divider,
    Drawer,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';

const PRIMARY = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

export default function ResponsiveFiltersPanel({
    subtitle,
    activeFiltersCount = 0,
    onClear,
    clearLabel = 'Limpar',
    filterButtonLabel = 'Filtros avançados',
    filtersOpen = false,
    onToggleFilters,
    actions = null,
    children = null,
    mobileDrawerTitle = 'Filtros',
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileOpen, setMobileOpen] = useState(false);

    const openMobile = () => setMobileOpen(true);
    const closeMobile = () => setMobileOpen(false);

    const buttonContent = useMemo(() => (
        <Badge
            badgeContent={activeFiltersCount}
            color="primary"
            sx={{
                '& .MuiBadge-badge': {
                    backgroundColor: PRIMARY,
                    fontSize: '0.62rem',
                    minWidth: 16,
                    height: 16,
                },
            }}
        >
            <Button
                size="small"
                startIcon={<FilterListIcon />}
                onClick={isMobile ? openMobile : onToggleFilters}
                variant={filtersOpen ? 'contained' : 'outlined'}
                sx={filtersOpen
                    ? {
                        backgroundColor: PRIMARY,
                        color: '#fff',
                        '&:hover': { backgroundColor: PRIMARY_DARK },
                        textTransform: 'none',
                        boxShadow: 'none',
                    }
                    : {
                        borderColor: '#ddd',
                        color: '#555',
                        textTransform: 'none',
                        '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` },
                    }}
            >
                {filterButtonLabel}
            </Button>
        </Badge>
    ), [activeFiltersCount, filterButtonLabel, filtersOpen, isMobile, onToggleFilters]);

    return (
        <>
            <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                {subtitle && (
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.78rem' }}>
                        {subtitle}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {activeFiltersCount > 0 && onClear && (
                        <Tooltip title="Limpar todos os filtros">
                            <Button
                                size="small"
                                onClick={onClear}
                                startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
                                sx={{
                                    textTransform: 'none',
                                    color: '#888',
                                    fontSize: '0.78rem',
                                    '&:hover': { backgroundColor: '#f5f5f5', color: '#444' },
                                }}
                            >
                                {clearLabel}
                            </Button>
                        </Tooltip>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', ml: 'auto' }}>
                        {buttonContent}
                        {actions}
                    </Box>
                </Box>
            </Box>

            {!isMobile && children}

            <Drawer
                anchor="bottom"
                open={mobileOpen}
                onClose={closeMobile}
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: 14,
                        borderTopRightRadius: 14,
                        maxHeight: '85dvh',
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#333' }}>
                        {mobileDrawerTitle}
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ p: 2, overflowY: 'auto' }}>
                    {children}
                </Box>
                <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={closeMobile}
                        sx={{
                            backgroundColor: PRIMARY,
                            '&:hover': { backgroundColor: PRIMARY_DARK },
                            textTransform: 'none',
                            boxShadow: 'none',
                            fontWeight: 600,
                        }}
                    >
                        Aplicar e fechar
                    </Button>
                </Box>
            </Drawer>
        </>
    );
}
